import { useAxios } from '@/hooks/useAxios';
import { mergeStatus, type QueryStatus, useQueries } from '@/utils/api';

/**
 * Per-plugin configured-status aggregator for the OSINT credential panels
 * (Human Intelligence Gathering Sources + Proxy / Infrastructure).
 *
 * The dedicated `/infrastructure/credential` list endpoint that the original
 * panels used has been removed. Configured-state now comes per plugin from the
 * enrichment master-key endpoint (`GET /enrichment/{plugin}/key`, response
 * `{ plugin, configured, maskedKey }`) — the same route + response shape as the
 * sibling `useMasterKey` hook. A master key present == the source is configured.
 *
 * `useQueries` fans one GET out per plugin id (rules-of-hooks-safe for a
 * variable-length allowlist), and the results are collapsed into the same
 * `{ data, status, refetch }` surface the panels previously consumed, so the
 * panel/card rendering stays unchanged. The query key (`['enrichment', plugin,
 * 'key']`) matches `useSetMasterKey`/`useDeleteMasterKey`, so a save or clear
 * auto-refreshes the corresponding card via their existing invalidation.
 *
 * Uses `useAxios({ doNotImpersonate: true })` because these are operator-global
 * Praetorian credentials, so impersonation is disabled.
 */

/** Response shape of `GET /enrichment/{plugin}/key`. */
interface EnrichmentKeyInfo {
  plugin: string;
  configured: boolean;
  maskedKey?: string;
}

/** Per-source configured flag, keyed by the plugin id used for the key routes. */
export interface OsintCredentialStatus {
  id: string;
  configured: boolean;
}

export interface OsintCredentialStatuses {
  data: OsintCredentialStatus[];
  status: QueryStatus;
  refetch: () => void;
}

export function useOsintCredentialStatuses(
  plugins: readonly string[]
): OsintCredentialStatuses {
  const axios = useAxios({ doNotImpersonate: true });

  const results = useQueries({
    queries: plugins.map(plugin => ({
      queryKey: ['enrichment', plugin, 'key'],
      queryFn: async () => {
        const { data } = await axios.get<EnrichmentKeyInfo>(
          `/enrichment/${plugin}/key`
        );
        return data;
      },
    })),
  });

  const data = plugins.map((id, index) => ({
    id,
    configured: results[index]?.data?.configured === true,
  }));

  const status = mergeStatus(...results.map(result => result.status));

  const refetch = () => {
    results.forEach(result => result.refetch());
  };

  return { data, status, refetch };
}
